import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { enrollmentAPI } from '../../services/api';
import Modal from '../../components/Modal';
import { LoadingSpinner, EmptyState } from '../../components/UI';

const MyCourses = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [quizModal, setQuizModal] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await enrollmentAPI.mine();
      setEnrollments(data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleLessonComplete = async (enrollmentId, lessonId) => {
    try {
      const { data } = await enrollmentAPI.updateProgress(enrollmentId, lessonId);
      toast.success('Lesson marked complete');
      if (data.autoCertIssued) {
        toast.success('🏆 Certificate issued! Check My Certificates.', { autoClose: 5000 });
      }
      fetchData();
      if (active) {
        const updated = (await enrollmentAPI.mine()).data.find((e) => e._id === enrollmentId);
        setActive(updated);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1 style={{ color: 'var(--green)' }}>My Courses</h1>
          <p className="subtitle">{enrollments.length} enrolled</p>
        </div>
      </div>

      {enrollments.length === 0 ? (
        <EmptyState icon="📖" title="No courses yet" message="Browse the catalog to enroll" />
      ) : (
        <div className="grid grid-2">
          {enrollments.map((e) => (
            <div key={e._id} className="card">
              <div className="flex justify-between items-center mb-2">
                <span className={`badge badge-${e.status}`}>{e.status}</span>
                {e.quizPassed && <span className="badge badge-completed">Quiz: {e.quizScore}%</span>}
              </div>
              <h3>{e.course?.title}</h3>
              <p className="text-muted mt-1" style={{ fontSize: '0.9rem' }}>
                Trainer: {e.course?.trainer?.name || 'TBA'} · {e.course?.modules?.length || 0} modules
              </p>
              <div className="mt-2">
                <div className="flex justify-between" style={{ fontSize: '0.85rem' }}>
                  <span>Progress</span>
                  <strong>{e.progress}%</strong>
                </div>
                <div className="progress mt-1"><div className="progress-bar" style={{ width: `${e.progress}%` }}></div></div>
              </div>
              {e.status === 'approved' && (
                <button className="btn btn-primary mt-2" style={{ width: '100%' }} onClick={() => setActive(e)}>
                  Continue learning →
                </button>
              )}
              {e.status === 'pending' && (
                <p className="text-muted mt-2 text-center" style={{ fontSize: '0.85rem' }}>⏳ Waiting for admin approval</p>
              )}
              {e.status === 'rejected' && (
                <p className="text-center mt-2" style={{ color: 'var(--danger)', fontSize: '0.85rem' }}>❌ Enrollment rejected</p>
              )}
              {e.status === 'completed' && (
                <p className="text-center mt-2" style={{ color: 'var(--success)', fontSize: '0.85rem' }}>✅ Completed!</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Course content modal */}
      <Modal open={!!active} onClose={() => setActive(null)} title={active?.course?.title} size="lg">
        {active && (
          <div>
            <div className="flex justify-between mb-2">
              <span>Progress: <strong>{active.progress}%</strong></span>
              {active.progress === 100 && active.course?.quiz?.questions?.length > 0 && !active.quizPassed && (
                <button className="btn btn-success btn-sm" onClick={() => { setActive(null); setQuizModal(active); }}>
                  Take Quiz →
                </button>
              )}
            </div>
            <div className="progress mb-2"><div className="progress-bar" style={{ width: `${active.progress}%` }}></div></div>

            {active.course?.modules?.map((mod, mi) => (
              <div key={mi} className="card mb-2" style={{ background: 'var(--bg)' }}>
                <h4>{mi + 1}. {mod.title}</h4>
                {mod.description && <p className="text-muted mt-1" style={{ fontSize: '0.85rem' }}>{mod.description}</p>}
                <div className="mt-2">
                  {mod.lessons.map((lesson) => {
                    const done = active.completedLessons?.some((id) => id === lesson._id);
                    return (
                      <div key={lesson._id} className="flex justify-between items-center" style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
                        <div>
                          <strong style={{ textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.6 : 1 }}>
                            {done ? '✓' : '○'} {lesson.title}
                          </strong>
                          <small className="text-muted" style={{ display: 'block' }}>{lesson.duration} min</small>
                        </div>
                        {!done && (
                          <button className="btn btn-secondary btn-sm" onClick={() => handleLessonComplete(active._id, lesson._id)}>
                            Mark done
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <QuizModal
        enrollment={quizModal}
        onClose={() => setQuizModal(null)}
        onSubmitted={fetchData}
      />
    </div>
  );
};

const QuizModal = ({ enrollment, onClose, onSubmitted }) => {
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (enrollment?.course?.quiz?.questions) {
      setAnswers(new Array(enrollment.course.quiz.questions.length).fill(null));
      setResult(null);
    }
  }, [enrollment]);

  if (!enrollment) return null;
  const questions = enrollment.course?.quiz?.questions || [];

  const handleSubmit = async () => {
    if (answers.includes(null)) return toast.error('Answer all questions');
    setSubmitting(true);
    try {
      const { data } = await enrollmentAPI.submitQuiz(enrollment._id, answers);
      setResult(data);
      toast[data.passed ? 'success' : 'warning'](
        data.passed ? `Passed with ${data.score}%! 🎉` : `Scored ${data.score}%. Try again.`
      );
      if (data.autoCertIssued) {
        toast.success('🏆 Certificate issued! Check My Certificates.', { autoClose: 5000 });
      }
      onSubmitted();
    } catch (e) {
      toast.error('Quiz submission failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Modal open={!!enrollment} onClose={onClose} title={enrollment.course?.quiz?.title || 'Quiz'} size="lg">
      {result ? (
        <div className="text-center">
          <h2 style={{ color: result.passed ? 'var(--success)' : 'var(--danger)' }}>
            {result.passed ? '🎉 Passed!' : '😞 Not yet'}
          </h2>
          <p className="mt-2">Score: <strong>{result.score}%</strong></p>
          <p className="text-muted mt-1">Passing: {enrollment.course?.quiz?.passingScore}%</p>
          {result.passed && result.autoCertIssued && (
            <div className="mt-2" style={{ background: 'var(--success-soft)', padding: '1rem', borderRadius: 8, color: 'var(--success)' }}>
              🏆 <strong>Certificate issued automatically!</strong>
              <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                Visit "My Certificates" to download it as PDF.
              </div>
            </div>
          )}
          {result.passed && !result.autoCertIssued && (
            <p className="mt-2 text-muted">
              Your trainer or admin will issue the certificate shortly.
            </p>
          )}
          <button className="btn btn-primary mt-3" onClick={onClose}>Close</button>
        </div>
      ) : (
        <>
          {questions.map((q, qi) => (
            <div key={qi} className="quiz-question">
              <div className="q-text">{qi + 1}. {q.question}</div>
              {q.options.map((opt, oi) => (
                <div
                  key={oi}
                  className={`quiz-option ${answers[qi] === oi ? 'selected' : ''}`}
                  onClick={() => {
                    const a = [...answers];
                    a[qi] = oi;
                    setAnswers(a);
                  }}
                >
                  <input type="radio" checked={answers[qi] === oi} readOnly />
                  <span>{opt}</span>
                </div>
              ))}
            </div>
          ))}
          <button className="btn btn-primary mt-2" onClick={handleSubmit} disabled={submitting} style={{ width: '100%' }}>
            {submitting ? 'Submitting…' : 'Submit Quiz'}
          </button>
        </>
      )}
    </Modal>
  );
};

export default MyCourses;
